import * as Yup from 'yup';

import { TypeOfUser } from '../helpers/Constants';

import { normalizePharmacistLicenseNumber } from './license';
import {
  OnboardingProfileSection,
  OnboardingProfileSectionFields,
  SaveOnboardingDetailsParameters,
  SoftwareExperiencePercentageInput,
  HandleOptionalResumeParameters,
  ShowOnboardingRecapParameters,
  UploadProfilePictureParameters,
  UploadTechnicianDiplomaParameters,
  UpdateProfileNamesParameters,
  VerifyPharmacistLicenseParameters,
} from './types';

type ValidationResult<T> = {
  success: true;
  data: T;
} | {
  success: false;
  error: Yup.ValidationError;
};

export const formatValidationErrors = (error: Yup.ValidationError) => {
  const issues = error.inner.length > 0 ? error.inner : [error];
  return issues.map(({ path, message }) =>
    `${path || 'input'}: ${message}`);
};

const nonEmptyString = () =>
  Yup.string()
    .required('Required')
    .test(
      'non-empty',
      'Required',
      (value) =>
        Boolean(value?.trim()),
    );

const finiteNumber = () =>
  Yup.number()
    .required('Required')
    .test(
      'finite',
      'Must be a finite number',
      (value) =>
        value === null
        || value === undefined
        || Number.isFinite(value),
    );

const nonNegativeNumber = () =>
  finiteNumber().min(0, 'Must be greater than or equal to 0');

const nonNegativeInteger = () =>
  nonNegativeNumber().integer('Must be an integer');

const fourDigitYear = () =>
  finiteNumber()
    .integer('Must be an integer')
    .min(1000, 'Must be a four-digit year')
    .max(9999, 'Must be a four-digit year');

const stringArray = (minimumItems = 0) =>
  Yup.array()
    .of(nonEmptyString())
    .min(
      minimumItems,
      `Must contain at least ${minimumItems} string`,
    )
    .required('Required');

const isPresent = (value: unknown) =>
  value !== null && value !== undefined;

const isNonEmptyString = (value: unknown) =>
  typeof value === 'string' && Boolean(value.trim());

export const validateClientToolParameters = async <T>(
  schema: Yup.Schema<T>,
  parameters: unknown,
): Promise<ValidationResult<T>> => {
  try {
    const data = await schema.validate(parameters, {
      abortEarly: false,
      strict: true,
      stripUnknown: false,
    });
    return { success: true, data };
  } catch (error) {
    if (error instanceof Yup.ValidationError) {
      return { success: false, error };
    }

    throw error;
  }
};

export const verifyPharmacistLicenseSchema:
Yup.ObjectSchema<VerifyPharmacistLicenseParameters> = Yup.object({
  licenseData: Yup.object({
    licenseNumber: nonEmptyString().test(
      'six-digit-license',
      'Must be a six-digit licence number',
      (value) =>
        Boolean(
          value
          && /^\d{6}$/.test(normalizePharmacistLicenseNumber(value)),
        ),
    ),
    firstName: Yup.string()
      .optional()
      .test(
        'non-empty-optional-first-name',
        'Required',
        (value) => value === undefined || Boolean(value.trim()),
      ),
    lastName: Yup.string()
      .optional()
      .test(
        'non-empty-optional-last-name',
        'Required',
        (value) => value === undefined || Boolean(value.trim()),
      ),
  }).test(
    'complete-name',
    'First and last name must be provided together',
    (value) =>
      Boolean(value)
      && (Boolean(value.firstName) === Boolean(value.lastName)),
  ).noUnknown().required(),
}).noUnknown().required().strict();

export const updateProfileNamesSchema:
Yup.ObjectSchema<UpdateProfileNamesParameters> = Yup.object({
  profileNames: Yup.object({
    firstName: nonEmptyString(),
    lastName: nonEmptyString(),
  }).noUnknown().required(),
}).noUnknown().required().strict();

const onboardingDetailsFieldsSchema: Yup.ObjectSchema<
  SaveOnboardingDetailsParameters['fields']
> = Yup.object({
  role: Yup.mixed<
    | TypeOfUser.pharmacist
    | TypeOfUser.technician
    | TypeOfUser.student
  >()
    .oneOf([
      TypeOfUser.pharmacist,
      TypeOfUser.technician,
      TypeOfUser.student,
    ])
    .required('Required'),
  preferdLanguage: nonEmptyString(),
  firstName: nonEmptyString(),
  lastName: nonEmptyString(),
  professionalCapacityId: nonEmptyString(),
  licenseNumber: Yup.string()
    .nullable()
    .optional()
    .test(
      'license-number-for-pharmacist',
      'Required for pharmacist',
      (value, context) =>
        context.parent.role !== TypeOfUser.pharmacist
        || Boolean(value?.trim()),
    ),
  graduationYear: fourDigitYear()
    .nullable()
    .optional()
    .test(
      'graduation-year-for-professional',
      'Required for pharmacist and technician',
      (value, context) =>
        context.parent.role === TypeOfUser.student || isPresent(value),
    ),
  yearsOfExperience: nonNegativeNumber()
    .nullable()
    .optional(),
  englishLevel: Yup.mixed<0 | 1 | 2>()
    .oneOf([0, 1, 2])
    .required('Required'),
  frenchLevel: Yup.mixed<0 | 1 | 2>()
    .oneOf([0, 1, 2])
    .required('Required'),
  otherLanguages: Yup.string().defined(),
  incorporated: Yup.boolean().required('Required'),
  isDSQKeyAvailable: Yup.boolean().required('Required'),
  isMethadonePrepKnowledge: Yup.boolean().required('Required'),
  isComfortDispillVerification: Yup.boolean().required('Required'),
  selectedSoftwares: Yup.array()
    .of(
      Yup.object({
        softwareId: nonEmptyString(),
        expLevel: Yup.mixed<1 | 2 | 3 | 4 | 5>()
          .oneOf([1, 2, 3, 4, 5])
          .required('Required'),
      }).noUnknown().required(),
    )
    .required('Required'),
  selectedContactPreferences: stringArray(1),
  address: nonEmptyString(),
  city: nonEmptyString(),
  province: nonEmptyString(),
  postalCode: nonEmptyString(),
  phone: Yup.string().optional(),
  mobile: nonEmptyString(),
  preferredWorkingDays: Yup.array()
    .of(nonNegativeInteger())
    .min(1, 'Required')
    .required('Required'),
  preferredReplacementDuration: nonEmptyString(),
  availabilityReminder: Yup.boolean().optional(),
  preferredDistanceRange: nonNegativeNumber(),
  remoteRegion: Yup.boolean().required('Required'),
  latitude: Yup.number().nullable().optional(),
  longitude: Yup.number().nullable().optional(),
  diploma: Yup.string()
    .nullable()
    .optional()
    .test(
      'diploma-for-technician',
      'Required for technician',
      (value, context) =>
        context.parent.role !== TypeOfUser.technician
        || isNonEmptyString(value),
    ),
  instituteName: Yup.string()
    .nullable()
    .optional()
    .test(
      'institute-for-technician',
      'Required for technician',
      (value, context) =>
        context.parent.role !== TypeOfUser.technician
        || isNonEmptyString(value),
    ),
  techGraduationYear: fourDigitYear()
    .nullable()
    .optional()
    .test(
      'diploma-year-for-technician',
      'Required for technician',
      (value, context) =>
        context.parent.role !== TypeOfUser.technician || isPresent(value),
    ),
  certificateFileName: Yup.string().nullable().optional(),
}).noUnknown().required();

const remainingSoftwarePercentageSchema = () =>
  Yup.mixed<SoftwareExperiencePercentageInput>()
    .oneOf(['0', '25', '50', '75', '100'])
    .optional();

export const saveOnboardingDetailsSchema:
Yup.ObjectSchema<SaveOnboardingDetailsParameters> = Yup.object({
  sectionName: Yup.mixed<'onboardingDetails'>()
    .oneOf(['onboardingDetails'])
    .required('Required'),
  fields: onboardingDetailsFieldsSchema,
  remainingSoftwarePercentage: remainingSoftwarePercentageSchema(),
}).noUnknown().required().strict();

export const onboardingProfileSectionFieldNames: Record<
OnboardingProfileSection,
Array<keyof OnboardingProfileSectionFields>
> = {
  [OnboardingProfileSection.ContactAndExperience]: [
    'address',
    'city',
    'province',
    'postalCode',
    'mobile',
    'selectedContactPreferences',
    'preferredReplacementDuration',
    'professionalCapacityId',
    'selectedSoftwares',
  ],
  [OnboardingProfileSection.Qualifications]: [
    'incorporated',
    'isDSQKeyAvailable',
    'frenchLevel',
    'englishLevel',
    'otherLanguages',
  ],
  [OnboardingProfileSection.Availability]: [
    'preferredWorkingDays',
    'preferredDistanceRange',
    'remoteRegion',
    // The year the pharmacist started relief work; yearsOfExperience is
    // calculated from it.
    'graduationYear',
    'yearsOfExperience',
    'isMethadonePrepKnowledge',
    'isComfortDispillVerification',
    'diploma',
    'instituteName',
    'techGraduationYear',
  ],
};

// The role is trusted signup context rather than model input, but the section
// keeps it so that role-specific rules such as the graduation year still apply.
const createProfileSectionSchema = (sectionName: OnboardingProfileSection) =>
  Yup.object({
    fields: onboardingDetailsFieldsSchema.pick([
      'role',
      ...onboardingProfileSectionFieldNames[sectionName],
    ]),
    remainingSoftwarePercentage: remainingSoftwarePercentageSchema(),
  }).noUnknown().required().strict();

export const onboardingProfileSectionSchemas = {
  [OnboardingProfileSection.ContactAndExperience]: createProfileSectionSchema(
    OnboardingProfileSection.ContactAndExperience,
  ),
  [OnboardingProfileSection.Qualifications]: createProfileSectionSchema(
    OnboardingProfileSection.Qualifications,
  ),
  [OnboardingProfileSection.Availability]: createProfileSectionSchema(
    OnboardingProfileSection.Availability,
  ),
};

export const validateOnboardingDetailsFields = async (
  fields: SaveOnboardingDetailsParameters['fields'],
) => {
  const validation = await validateClientToolParameters(
    saveOnboardingDetailsSchema,
    {
      fields,
      sectionName: 'onboardingDetails',
    },
  );

  return validation.success
    ? []
    : formatValidationErrors(validation.error);
};

export const uploadProfilePictureSchema:
Yup.ObjectSchema<UploadProfilePictureParameters> = Yup.object({
  profilePictureData: Yup.object({
    openUpload: Yup.boolean()
      .oneOf([true], 'Upload dialog must be opened')
      .required('Required'),
  }).noUnknown().required(),
}).noUnknown().required().strict();

export const handleOptionalResumeSchema:
Yup.ObjectSchema<HandleOptionalResumeParameters> = Yup.object({
  resumeData: Yup.object({
    shouldUpload: Yup.boolean().required('Required'),
  }).noUnknown().required(),
}).noUnknown().required().strict();

export const uploadTechnicianDiplomaSchema:
Yup.ObjectSchema<UploadTechnicianDiplomaParameters> = Yup.object({
  diplomaData: Yup.object({
    openUpload: Yup.boolean()
      .oneOf([true], 'Upload dialog must be opened')
      .required('Required'),
  }).noUnknown().required(),
}).noUnknown().required().strict();

export const showOnboardingRecapSchema:
Yup.ObjectSchema<ShowOnboardingRecapParameters> = Yup.object({
  recapData: Yup.object({
    openRecap: Yup.boolean()
      .oneOf([true])
      .required('Required'),
  }).noUnknown().required(),
}).noUnknown().required().strict();
